// pages/monthly-tests/ResultSheet.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Paper, Typography, Grid, Card, CardContent, Button,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, CircularProgress, Alert, Divider, Stack, Avatar
} from '@mui/material';
import { format } from 'date-fns';
import { normalizeResultSheet, computeStats } from '../../utils/normalizeResultSheet';

const ResultSheet = ({ receiptData, setTemID }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [meta, setMeta] = useState(null);
    const [rows, setRows] = useState([]);

    const receiptId = `test-marks`;

    useEffect(() => {
        if (setTemID) {
            setTemID(receiptId);
        }
        const fetchData = async () => {
            setLoading(true);
            setError('');
            try {
                const raw = receiptData;
                const { meta: m, rows: r } = normalizeResultSheet(raw);
                setMeta(m);
                setRows(r);
            } catch (err) {
                console.error(err);
                setError(err?.response?.data?.message || 'Failed to load result sheet');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [receiptData, setTemID, receiptId]);

    const stats = useMemo(
        () => (meta ? computeStats(rows, meta.passingMarks) : null),
        [meta, rows]
    );

    if (loading) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!meta || rows.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="info">No result data available.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* ---------- Printable area ---------- */}
            <Paper id={receiptId}
                sx={{
                    p: 4,
                    bgcolor: '#fff',
                    color: '#000',
                    '@media print': { boxShadow: 'none', p: 0 },
                }}
            >
                {/* Header */}
                <Box display="flex" alignItems="center" gap={2} mb={2} color={'#000'}>
                    {meta.logo && (
                        <Avatar
                            src={meta.logo}
                            alt="logo"
                            variant="rounded"
                            sx={{ width: 64, height: 64 }}
                        />
                    )}
                    <Box flex={1} textAlign={meta.logo ? 'center' : 'center'}>
                        <Typography variant="h6" fontWeight="bold">
                            {meta.schoolName}
                        </Typography>
                        {meta.address && (
                            <Typography variant="body2">{meta.address}</Typography>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 1, borderColor: '#000' }} />

                <Typography variant="subtitle1" align="center" fontWeight="bold" mt={1} color='#000'>
                    MONTHLY TEST RESULT — {meta.academicYear}
                </Typography>

                {/* Test meta */}
                <Box
                    display="grid"
                    gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr 1fr' }}
                    gap={1}
                    my={2}
                    color={'#000'}
                >
                    <Typography variant="body2"><b>Test:</b> {meta.testName}</Typography>
                    <Typography variant="body2"><b>Subject:</b> {meta.subject}</Typography>
                    <Typography variant="body2">
                        <b>Date:</b>{' '}
                        {meta.testDate ? format(new Date(meta.testDate), 'dd MMM yyyy') : '-'}
                    </Typography>
                    <Typography variant="body2">
                        <b>Class:</b> {meta.standard} - {meta.section}
                    </Typography>
                    <Typography variant="body2"><b>Max Marks:</b> {meta.maxMarks}</Typography>
                    <Typography variant="body2"><b>Passing:</b> {meta.passingMarks}</Typography>
                </Box>

                {/* Marks table */}
                <TableContainer>
                    <Table size="small" sx={{ border: '1px solid #000' }}>
                        <TableHead>
                            <TableRow>
                                {['#', 'Roll No', 'Student Name', 'Marks', '%', 'Grade', 'Result', 'Remarks'].map(h => (
                                    <TableCell
                                        key={h}
                                        align={['#', 'Roll No', 'Marks', '%', 'Grade', 'Result'].includes(h) ? 'center' : 'left'}
                                        sx={{ border: '1px solid #000', fontWeight: 'bold', bgcolor: '#f0f0f0', color: '#000' }}
                                    >
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.marksId ?? r.studentId}>
                                    <TableCell sx={{ border: '1px solid #000', color: '#000' }} align="center">{r.seq}</TableCell>
                                    <TableCell sx={{ border: '1px solid #000', color: '#000' }} align="center">{r.rollNumber}</TableCell>
                                    <TableCell sx={{ border: '1px solid #000', color: '#000' }}>{r.studentName}</TableCell>
                                    <TableCell sx={{ border: '1px solid #000', color: '#000' }} align="center">
                                        {r.absent ? 'AB' : r.marksObtained}
                                    </TableCell>
                                    <TableCell sx={{ border: '1px solid #000', color: '#000' }} align="center">
                                        {r.absent ? '-' : `${r.percentage}%`}
                                    </TableCell>
                                    <TableCell sx={{ border: '1px solid #000', color: '#000' }} align="center">{r.grade}</TableCell>
                                    <TableCell sx={{ border: '1px solid #000', color: '#000' }} align="center">{r.result}</TableCell>
                                    <TableCell sx={{ border: '1px solid #000', color: '#000' }}>{r.remarks}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Summary */}
                <Box mt={2} display="flex" gap={3} flexWrap="wrap">
                    <Typography variant="body2"><b>Appeared:</b> {stats.appeared}</Typography>
                    <Typography variant="body2"><b>Passed:</b> {stats.passed}</Typography>
                    <Typography variant="body2"><b>Failed:</b> {stats.failed}</Typography>
                    <Typography variant="body2"><b>Absent:</b> {stats.absent}</Typography>
                    <Typography variant="body2"><b>Average:</b> {stats.average}</Typography>
                    <Typography variant="body2"><b>Pass %:</b> {stats.passPercentage}%</Typography>
                </Box>

                {/* Signatures */}
                <Box mt={6} display="flex" justifyContent="space-between">
                    <Typography variant="caption">Class Teacher</Typography>
                    <Typography variant="caption">Subject Teacher</Typography>
                    <Typography variant="caption">Principal</Typography>
                </Box>
            </Paper>
        </Box>
    );
};

export default ResultSheet;